// filepath: apps/web/src/features/observability/DrilldownLink.tsx
//
// FR-UI-DASH-1 AC-5 -- drill-down to the failed-instance dashboard.
//
// Builds a URLSearchParams-typed URL pointing at the FR-UI-PROC-1
// failed-instance dashboard with `from=`, `to=`, and `metric=`
// query parameters. The link navigates to
// `/processes/instances/failed?from=<iso>&to=<iso>&metric=<id>`,
// reusing the existing FR-UI-PROC-1 route target.
//
// INV-1: the drill-down URL never embeds `tenantSlug` -- the tenant
// is resolved at runtime via the OIDC realm + hostname (no URL-
// parameter leakage). The drill-down `metric` value is constrained
// to the typed `MetricId` enum -- a runtime guard rejects any value
// that does not match.
//
// INV-9: the link's label is a text node (no `dangerouslySetInnerHTML`).
// The link uses a real <a> element with an onClick handler that
// prefers `history.pushState` over `window.location.assign` to keep
// the SPA single-page nav (mirrors FR-UI-PROC-1's ProcRoute pattern).

import React from "react";
import { METRIC_IDS, type MetricId } from "./types";

export interface DrilldownLinkProps {
  from: string;
  to: string;
  metric: MetricId;
  onNavigate?: (params: { from: string; to: string; metric: string }) => void;
  label: string;
}

function isValidMetricId(metric: string): metric is MetricId {
  return (METRIC_IDS as readonly string[]).includes(metric);
}

export function DrilldownLink({
  from,
  to,
  metric,
  onNavigate,
  label,
}: DrilldownLinkProps): React.ReactElement {
  const onClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    if (!isValidMetricId(metric)) {
      e.preventDefault();
      return;
    }
    if (onNavigate !== undefined) {
      e.preventDefault();
      onNavigate({ from, to, metric });
      return;
    }
    if (typeof window === "undefined") return;
    e.preventDefault();
    const params = new URLSearchParams();
    params.set("from", from);
    params.set("to", to);
    params.set("metric", metric);
    window.history.pushState(
      {},
      "",
      `/processes/instances/failed?${params.toString()}`,
    );
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  const href = isValidMetricId(metric)
    ? `/processes/instances/failed?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&metric=${encodeURIComponent(metric)}`
    : "/processes/instances/failed";

  return (
    <a
      href={href}
      className="observability-drilldown-link"
      data-testid="observability-drilldown-link"
      data-metric={metric}
      onClick={onClick}
    >
      {label}
    </a>
  );
}
